import React, { useCallback, useEffect, useState, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { Section } from '@atlaskit/side-navigation';
import { token } from '@atlaskit/tokens';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { ExpandButton } from '../expand-button/index.tsx';

export type ExpandableSectionProps = {
	isCollapsed?: boolean;
	hasSeparator?: boolean;
	children: ReactNode;
	testId?: string;
	title?: string;
	submenuActive?: boolean;
	removeTitleSection?: boolean;
	// changed the below from onExpandClick to expandClick as the former
	// was failing unit tests :/
	// eslint-disable-next-line jira/react/handler-naming
	expandClick?: () => void;
	context?: string;
};

export const ExpandableSection = (props: ExpandableSectionProps) => {
	const {
		title = '',
		submenuActive = false,
		isCollapsed = false,
		hasSeparator = false,
		removeTitleSection = false,
		testId = '',
		expandClick,
		context,
	} = props;
	const [expanded, setExpanded] = useState<boolean>(true);
	const [showCaret, setShowCaret] = useState<boolean>(!expanded && !submenuActive);
	const [removeCaret, setRemoveCaret] = useState<boolean>(submenuActive);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const expandButtonId = title != null ? title.replace(/ /g, '-').toLowerCase() : '';

	const onContainerMouseOver = useCallback(() => {
		if (expanded && !submenuActive) {
			setShowCaret(true);
		}
		if (submenuActive) {
			setShowCaret(false);
		}
	}, [expanded, submenuActive]);

	const onContainerMouseLeave = useCallback(() => {
		if (expanded) {
			setShowCaret(false);
		}
	}, [expanded]);

	const onExpandButtonClick = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'expandButton changed', 'navModules', {
			navModulesId: title,
			expanded: !expanded,
		});
		setExpanded(!expanded);
		if (expandClick) {
			expandClick();
		}
	}, [createAnalyticsEvent, title, expanded, expandClick]);

	useEffect(() => {
		if (!expanded) {
			setShowCaret(true);
		}
	}, [expanded]);

	useEffect(() => {
		if (submenuActive) {
			setRemoveCaret(true);
		} else {
			setRemoveCaret(false);
		}
	}, [submenuActive]);

	useEffect(() => {
		setExpanded(!isCollapsed);
	}, [isCollapsed]);

	useEffect(() => {
		if (removeTitleSection) {
			setExpanded(true);
		}
	}, [removeTitleSection]);

	return (
		<Container
			onMouseOver={onContainerMouseOver}
			onMouseLeave={onContainerMouseLeave}
			submenuActive={submenuActive}
			removeTitleSection={removeTitleSection}
			context={context}
		>
			<ExpandButton
				isExpanded={expanded || removeTitleSection}
				id={expandButtonId}
				isActive
				label={props.title || ''}
				onClick={onExpandButtonClick}
				size="medium"
				isVisible={showCaret}
				removeCaret={removeCaret || removeTitleSection}
				tabIndex={0}
			/>
			<Section hasSeparator={hasSeparator} testId={testId} title={title}>
				{expanded && props.children}
			</Section>
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{
	submenuActive: boolean;
	removeTitleSection: boolean;
	context?: string;
}>(
	{
		position: 'relative',
		display: 'flex',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		marginTop: (props) => (props.submenuActive || props.removeTitleSection ? 0 : '14px'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		flexDirection: (props) => (props.submenuActive ? 'column' : 'row'),
		/*
            the height attribute below allows the scroll bar to show when entering a nested menu
            If you remove, the scrolling will no longer work in nested menus
        */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		height: (props) => (props.submenuActive ? '100%' : 'auto'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginLeft: '-15px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"div[role='group']": {
			display: 'block',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
			width: (props) =>
				props.submenuActive || props.removeTitleSection ? '100%' : 'calc(100% - 16px)',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			"& div[data-ds--menu--heading-item='true']": {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
				marginTop: (props) => (props.submenuActive ? '14px' : `${token('space.negative.075')}`),
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
				paddingLeft: (props) => (props.submenuActive ? '10px' : 0),
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
				display: (props) => (props.removeTitleSection ? 'none' : 'block'),
				color: token('color.text.subtle'),
			},
			/*  the below selectors target all variations of menu items <MenuItem /> which can be
                a, button or either nested inside a span -> if the nested menu is not open, the left negative margin
                (which accounts for the expand button) needs to be removed so menu items appear correctly in nested
                menus.
            */
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'& > a, & > span > a, & > button, & > span > button': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
				marginRight: (props) => (props.removeTitleSection ? 0 : '7px'),
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				borderRadius: `${Math.round(borderRadius * 1.333)}px`,
			},
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => props.removeTitleSection && { "div[role='group']": { '&::before': { height: 0 } } },
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		!props.submenuActive && {
			marginRight: token('space.negative.075'),

			borderRadius: '6px',
			paddingTop: token('space.100'),
			marginTop: token('space.025'),
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			paddingRight: '5px',
			borderWidth: '2px',
			borderStyle: 'solid',
			borderColor: 'transparent',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => props.removeTitleSection && { paddingTop: 0, marginTop: 0 },
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => props.submenuActive && { marginLeft: 0 },
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) => !props.submenuActive && props.removeTitleSection && { marginLeft: '11px' },
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		!props.submenuActive &&
		!props.removeTitleSection &&
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		props.context !== 'classic' && { marginLeft: '-15px' },
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props) =>
		!props.submenuActive &&
		!props.removeTitleSection &&
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		props.context === 'classic' && { marginLeft: '-14px' },
);
